import { getPageRange } from './pageUtils';
import { isConnectedAccount } from 'owa-accounts-store';
import type { PageQueryResponse } from 'owa-availability-service';
import { lazyGetAvailabilityFunction } from 'owa-availability-service';
import { SLOT_SIZE_IN_MINUTES, MAX_BATCH_SIZE } from '../constants';
import type { OwaDate } from 'owa-datetime';
import { isEqual } from 'owa-datetime';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * BASELINE. Do not copy and paste!
 *	> 'lodash-es/debounce' import is restricted from being used by a pattern. Lodash can cause bundle bloat and performance problems with loaf. Use native function instead */
import debounce from 'lodash-es/debounce';
import type { MailboxInfo } from 'owa-client-types';
import { isAccountFeatureEnabled } from 'owa-feature-flags';

let pagesRequestedTime: OwaDate | undefined = undefined;
let requestedAddressesForSamePage: string[] = [];

// Request f/b of single address. If it is organizer, the request will be out immediately;
// Otherwise, the address is put in a queue. Once the query page index has changed, or the
// queue reaches the size limit, or an amount of time has elapsed since the first address
// was added to the queue, we send one request to query the status of all addresses in the queue.
export function getAvailabilityForSingleAddress(
    address: string,
    pageIndex: number,
    mailboxInfo: MailboxInfo,
    processResponse: (pageQueryResponse: PageQueryResponse) => void,
    processWorkPlansResponse: (pageQueryResponse: PageQueryResponse) => void
): void {
    const { start, end } = getPageRange(pageIndex);

    const isCurrentUser =
        address == mailboxInfo.userIdentity || address == mailboxInfo.mailboxSmtpAddress;

    if (isCurrentUser || isConnectedAccount(address)) {
        // Never batch the current user. As their availability is much less likely to time out.
        lazyGetAvailabilityFunction.importAndExecute(
            start,
            end,
            [address],
            processResponse,
            true /* retryOnFail */,
            mailboxInfo,
            SLOT_SIZE_IN_MINUTES,
            undefined /* retryCount */
        );

        return;
    }

    // If there was a page jump, resolve availability for pending attendees and start a new batch.
    if (!pagesRequestedTime || !isEqual(pagesRequestedTime, start)) {
        debouncedGetAvailability.flush();
        pagesRequestedTime = start;
    }

    if (!requestedAddressesForSamePage) {
        requestedAddressesForSamePage = [address];
    } else {
        requestedAddressesForSamePage.push(address);
    }

    debouncedGetAvailability(
        start,
        end,
        requestedAddressesForSamePage.slice(),
        mailboxInfo,
        processResponse,
        processWorkPlansResponse
    );

    if (requestedAddressesForSamePage.length >= MAX_BATCH_SIZE) {
        debouncedGetAvailability.flush();
    }
}

const debouncedGetAvailability = debounce(
    (
        start: OwaDate,
        end: OwaDate,
        attendees: string[],
        mailboxInfo: MailboxInfo,
        processResponse: (pageQueryResponse: PageQueryResponse) => void,
        processWorkPlansResponse: (pageQueryResponse: PageQueryResponse) => void
    ) => {
        requestedAddressesForSamePage = [];

        lazyGetAvailabilityFunction.importAndExecute(
            start,
            end,
            attendees,
            processResponse,
            true /* retryOnFail */,
            mailboxInfo /* mailboxInfo */,
            SLOT_SIZE_IN_MINUTES,
            undefined /* retryCount */,
            false /* getWorkPlansOnly */
        );

        if (isAccountFeatureEnabled('cal-location-sharing-dropdown', mailboxInfo)) {
            // This separate call is to get valid FWH data regardless of the calendar sharing permissions
            // of the attendees. It exists as a workaround to the limitation of the GetSchedule API.
            lazyGetAvailabilityFunction.importAndExecute(
                start,
                end,
                attendees,
                processWorkPlansResponse,
                true /* retryOnFail */,
                mailboxInfo /* mailboxInfo */,
                SLOT_SIZE_IN_MINUTES,
                undefined /* retryCount */,
                true /* getWorkPlansOnly */
            );
        }
    },
    500
);
